
import {catchError} from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable ,  Subject } from 'rxjs';
import { ExceptionService, PaginatedList, UtilsService } from '../../../xform-compat';
import { CompanyBasic, CompanyBusinessAlert, CompanyCategory, CompanyEdit } from '../../../shared/models';
import { CONFIG } from '../../../config';


/**
 * Data service for Companies basic info
 */
@Injectable({
  providedIn: 'root'
})
export class CompanyService {
  private companyUpdate: Subject<any>;
  private url = '/api/rms/companies';

  constructor(
    private http: HttpClient,
    private utilsService: UtilsService,
    private exceptionService: ExceptionService
  ) {
    this.companyUpdate = new Subject<any>();
  }

  /**
   * Gets the company basic data
   * @param {number} companyId
   * @param params
   * @returns {Observable}
   */
  public getCompany(companyId: number, params?: any): Observable<CompanyBasic> {
    const httpParams: HttpParams = this.utilsService.generateParams(params);
    return this.http
      .get(`${this.url}/${companyId}`, {params: httpParams}).pipe(
      catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /**
   * Gets the company basic data
   * @param {number} companyId
   * @param params
   * @returns {Observable}
   */
  public getCompanyAcquisitions (companyId: number, params?: any) {
    const httpParams: HttpParams = this.utilsService.generateParams(params);
    return this.http
      .get(`${this.url}/${companyId}/acquisitions`, {params: httpParams}).pipe(
      catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /**
   * Gets a paginated list of companies
   * @param params
   * @returns {Observable}
   */
  public getCompanies (params?: any): any {
    const httpParams: HttpParams = this.utilsService.generateParams(params);
    return this.http
      .get(this.url, {params: httpParams}).pipe(
      catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /**
   * Returns the pricing info for companies using the companyId
   * @param params
   */
  public getCompaniesPricing (params?: any): Observable<PaginatedList<CompanyBasic>> {
    const httpParams: HttpParams = this.utilsService.generateParams(params);
    return this.http
      .get(`/api/rms/companies/multipricings`, {params: httpParams}).pipe(
      catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /**
   * Search over all the companies
   * @param params: search term `q` and pagination params
   * @returns {Observable<PaginatedList<CompanyBasic>>}
   */
  public searchCompanies (params?: any): Observable<PaginatedList<CompanyBasic>> {
    const httpParams: HttpParams = this.utilsService.generateParams(params);
    return this.http
      .get(`/api/rms/autocomplete/companies`, {params: httpParams}).pipe(
      catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /**
   * Get the list of company categories
   * @param params: query params
   * @returns {Observable<PaginatedList<CompanyCategory>>}
   */
  public getCompanyCategories (params?: any): Observable<PaginatedList<CompanyCategory>> {
    const httpParams: HttpParams = this.utilsService.generateParams(params);
    return this.http
      .get(`${CONFIG.apiUrl}/rms/companycategories`, {params: httpParams}).pipe(
      catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /**
   * Updates the basic info for a company
   * @param {number} companyId: company to update
   * @param {CompanyEdit} formData: data to update
   * @returns {Observable<CompanyEdit>}: updated response info
   */
  public editCompany (companyId: number, formData: CompanyEdit): Observable<any> {
    return this.http.put(`${this.url}/${companyId}/onboard`, formData)
      .pipe(catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /**
   * Update company business alerts to receive or mute notifications
   * @param companyId
   * @param body
   */
  public updateBusinessAlerts(companyId: number, body: {subscriptions: Array<CompanyBusinessAlert>}): Observable<CompanyBusinessAlert> {
    return this.http.put(`${this.url}/${companyId}/subscriptions`, body)
      .pipe(catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /**
   * Sends a message to the subscribed object on the component
   * @param message
   */
  public sendMessage(message: string) {
    this.companyUpdate.next({ text: message });
  }

  /**
   * Observable to listen when a message is sent
   */
  public getMessage(): Observable<any> {
    return this.companyUpdate.asObservable();
  }

  /**
   * Update the Courtship Type for a Company
   * @param companyId Company Identifier
   * @param courtshipType New Courtship type
   * @returns Update result
   */
  public updateCompanyCourtship(companyId: number, courtshipType: string): Observable<any> {
    return this.http.put(`${this.url}/${companyId}/courtship/${courtshipType}`, {})
      .pipe(catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /**
   * Turn a company's headlines on or off in headline search. Also triggers a reindex of the
   * company's headlines server-side, so the change is reflected in the index.
   * @param companyId Company identifier
   * @param allowHeadlines Whether the company's headlines should be searchable
   * @returns Update result
   */
  public updateAllowHeadlines(companyId: number, allowHeadlines: boolean): Observable<any> {
    return this.http.put(`${this.url}/${companyId}/allowheadlines/${allowHeadlines}`, {})
      .pipe(catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /**
   * Update the senior attention for a company
   * @param companyId Company identifier
   * @param seniorAttention New senior attention value
   * @returns Update result
   */
  public updateCompanySeniorAttention(companyId: number, seniorAttention: boolean): Observable<any> {
    return this.http.put(`${this.url}/${companyId}/seniorAttention/${seniorAttention}`, {})
      .pipe(catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

}
